import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import { useServices } from "../../hooks/useServices";

export default function DepositPage() {
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();

  const { fees }      = useTradingConfig({ enabled: isAuthenticated });
  const { isEnabled, getMessage } = useServices();

  const percentFee     = fees.depositFeePercent || 5;
  const fiatEnabled    = isEnabled("fiat_deposit");
  const cryptoEnabled  = isEnabled("deposits");

  // All deposit options with their service gate key.
  // accentTone maps onto the semantic token scale instead of fixed hex
  // pairs — fiat uses "brand" (it's the primary/preferred path, same
  // green as the rest of the app's brand chrome), crypto uses "purple"
  // to stay visually distinct as a secondary, on-chain pathway.
  const allOptions = [
    {
      id:          "fiat",
      serviceKey:  "fiat_deposit",
      title:       "Naira Deposit",
      subtitle:    "Fund with NGN via card, bank transfer or USSD",
      icon:        "ri-bank-card-line",
      badge:       "Instant",
      accentTone:  "brand",
      features: [
        { icon: "ri-bank-line",    text: "Bank transfer, card or USSD" },
        { icon: "ri-time-line",    text: "Credited within seconds" },
        { icon: "ri-percent-line", text: `${percentFee}% processing fee applies` },
      ],
      path: "/deposit-fiat",
    },
    {
      id:          "crypto",
      serviceKey:  "deposits",
      title:       "Crypto Deposit",
      subtitle:    "Send crypto directly to your wallet address",
      icon:        "ri-bit-coin-line",
      badge:       "On-chain",
      accentTone:  "purple",
      features: [
        { icon: "ri-shield-check-line",       text: "BTC, ETH, USDT, BNB & more" },
        { icon: "ri-qr-code-line",            text: "Scan QR or copy address" },
        { icon: "ri-arrow-right-circle-line", text: "Confirmed after network blocks" },
      ],
      path: "/deposit-crypto",
    },
  ];

  // Only show options whose service is enabled
  const visibleOptions = allOptions.filter(o => isEnabled(o.serviceKey));

  return (
    <div className="min-h-screen bg-bg pb-28 font-display">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-xl">Deposit</h1>
            <p className="text-muted text-xs mt-0.5">Choose how you want to fund your account</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-4">

        {/* No deposit methods available */}
        {visibleOptions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-tools-line text-muted text-2xl" />
            </div>
            <p className="text-primary font-semibold mb-1">Deposits Unavailable</p>
            <p className="text-muted text-sm max-w-xs leading-relaxed">
              {getMessage("fiat_deposit") || getMessage("deposits")}
            </p>
            <button onClick={() => navigate("/home")}
              className="mt-6 px-6 py-2.5 bg-brand text-on-brand font-semibold rounded-xl text-sm">
              Back to Home
            </button>
          </div>
        ) : (
          <>
            {/* Hero hint — only show if both are available */}
            {visibleOptions.length === 2 && (
              <div className="bg-surface border border-border rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-card">
                <div className="w-9 h-9 bg-brand-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-brand text-lg" />
                </div>
                <p className="text-muted text-xs leading-relaxed">
                  Naira deposits are instant and great for buying crypto.
                  Crypto deposits are on-chain and may take a few minutes.
                </p>
              </div>
            )}

            {/* Option cards — only enabled ones */}
            {visibleOptions.map(option => (
              <button key={option.id} onClick={() => navigate(option.path)}
                className="w-full bg-surface border border-border rounded-3xl p-5 text-left shadow-card hover:shadow-elevated hover:border-border-strong active:scale-[0.99] transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${option.accentTone}-light)` }}>
                      <i className={`${option.icon} text-2xl`} style={{ color: `var(--color-${option.accentTone})` }} />
                    </div>
                    <div>
                      <p className="text-primary font-bold text-base">{option.title}</p>
                      <p className="text-muted text-xs mt-0.5 leading-snug max-w-[180px]">{option.subtitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                      style={{ color: `var(--color-${option.accentTone}-text)`, background: `var(--color-${option.accentTone}-light)` }}>
                      {option.badge}
                    </span>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `var(--color-${option.accentTone}-light)` }}>
                      <i className="ri-arrow-right-line text-sm" style={{ color: `var(--color-${option.accentTone})` }} />
                    </div>
                  </div>
                </div>
                <div className="h-px bg-border mb-4" />
                <div className="space-y-2.5">
                  {option.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${option.accentTone}-light)` }}>
                        <i className={`${f.icon} text-xs`} style={{ color: `var(--color-${option.accentTone})` }} />
                      </div>
                      <span className="text-muted text-xs">{f.text}</span>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </>
        )}

        <div className="text-center pt-2 pb-4">
          <p className="text-muted text-xs flex items-center justify-center gap-1.5">
            <i className="ri-lock-2-line text-faint" />
            All deposits are secured with 256-bit encryption
          </p>
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
}